import { useCallback, useState } from "react";

import { ApiError } from "../api/client";
import { blockAuthor, unblockAuthor } from "../api/endpoints";
import { describeError } from "../api/errors";

/**
 * blocked   - the author was added now
 * already   - the author already was on the list
 * unblocked - the author was taken off the list
 * not_found - there was nobody to take off
 */
export type AuthorBlockResult = "blocked" | "already" | "unblocked" | "not_found";

interface AuthorBlockState {
  result: AuthorBlockResult | null;
  working: boolean;
  error: string | null;
}

export interface UseAuthorBlock extends AuthorBlockState {
  block: (authorId: number) => void;
  unblock: (authorId: number) => void;
}

/** A telegram user id as an operator types it: digits only, and not zero. */
export function parseAuthorId(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const authorId = Number(trimmed);
  return Number.isSafeInteger(authorId) && authorId > 0 ? authorId : null;
}

/**
 * The list of message authors who objected to their messages being processed.
 *
 * One step each way and no confirmation, unlike taking a chat off monitoring: nothing is
 * deleted, nobody is told, and a mistake is undone with the other button.
 *
 * The list is not enforced yet - chatsreader does not read it - and the screen shows that as a
 * warning after every successful addition, "already" included.
 */
export function useAuthorBlock(): UseAuthorBlock {
  const [state, setState] = useState<AuthorBlockState>({ result: null, working: false, error: null });

  const block = useCallback(async (authorId: number) => {
    setState({ result: null, working: true, error: null });
    try {
      const { newly_blocked } = await blockAuthor(authorId);
      setState({ result: newly_blocked ? "blocked" : "already", working: false, error: null });
    } catch (error) {
      setState({ result: null, working: false, error: describeError(error) });
    }
  }, []);

  const unblock = useCallback(async (authorId: number) => {
    setState({ result: null, working: true, error: null });
    try {
      await unblockAuthor(authorId);
      setState({ result: "unblocked", working: false, error: null });
    } catch (error) {
      // not being on the list is the state the operator asked for, so it is an answer and not
      // a failure
      if (error instanceof ApiError && error.status === 404) {
        setState({ result: "not_found", working: false, error: null });
        return;
      }
      setState({ result: null, working: false, error: describeError(error) });
    }
  }, []);

  return {
    ...state,
    block: (authorId: number) => void block(authorId),
    unblock: (authorId: number) => void unblock(authorId),
  };
}
